import React, { useRef, useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import { SliderField } from "./form/SettingsField";

interface ImageCropPickerProps {
  backgroundImage: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  onPositionChange: (position: string) => void;
  onZoomChange: (zoom: number) => void;
}

export const ImageCropPicker = ({
  backgroundImage,
  backgroundPosition,
  backgroundZoom,
  onPositionChange,
  onZoomChange,
}: ImageCropPickerProps) => {
  const [dragging, setDragging] = useState(false);
  const thumbRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(
    (clientX: number, clientY: number) => {
      const el = thumbRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((clientX - rect.left) / rect.width) * 100)));
      const y = Math.min(100, Math.max(0, Math.round(((clientY - rect.top) / rect.height) * 100)));
      onPositionChange(`${x}% ${y}%`);
    },
    [onPositionChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setDragging(true);
      updatePosition(e.clientX, e.clientY);

      const handleMouseMove = (ev: MouseEvent) => updatePosition(ev.clientX, ev.clientY);
      const handleMouseUp = () => {
        setDragging(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [updatePosition],
  );

  const pos = backgroundPosition || "50% 50%";
  const parts = pos.split(/\s+/);
  const xPct = parseFloat(parts[0]) || 50;
  const yPct = parseFloat(parts[1]) || 50;

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        Image Crop
      </Typography>
      <Box
        ref={thumbRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: "relative",
          width: "100%",
          height: 100,
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          cursor: dragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: pos,
            backgroundRepeat: "no-repeat",
            transform: `scale(${backgroundZoom || 1})`,
            transformOrigin: pos,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: `${xPct}%`,
            top: `${yPct}%`,
            transform: "translate(-50%, -50%)",
            width: 16,
            height: 16,
            borderRadius: "50%",
            border: "2px solid #fff",
            backgroundColor: "rgba(25, 118, 210, 0.7)",
            boxShadow: "0 0 4px rgba(0,0,0,0.5)",
            pointerEvents: "none",
          }}
        />
      </Box>

      <SliderField
        label="Zoom"
        value={backgroundZoom ?? 1}
        onChange={onZoomChange}
        min={1}
        max={2.5}
        step={0.1}
      />
    </Box>
  );
};
